// Send updates (docs/design/orgs.md 6.3): what a personal item's link to OEMUAP would send, as one
// draft the person edits. Pure, with no Node or database imports: the server builds the draft from the
// item's events, and the web app (and Claude's draft_updates) show and rebuild it from the same lines.
//
// "Since the last send" is exact: every send records the ids of the events it dealt with (sent, or
// left out on purpose), and the next draft is the collected events no send has dealt with. A line the
// person unticks is left out and doesn't come back.

import type { ItemKind, Stage } from "./shared.ts";

/** One event of the personal item (or of a sub-item of it), with what the draft needs to word it. */
export interface DraftEvent {
  /** ItemEvent.id, as text (it's a bigint) */
  id: string;
  at: string;
  kind: string;
  data: Record<string, unknown>;
  /** who did it (their name), and whether that's the person sending */
  actor: string | null;
  mine: boolean;
  /** the event is on a sub-item of the linked item: its title (never its number) */
  sub: { title: string; kind: ItemKind | null } | null;
  /** a note event: the note's text, or null when the note was removed since */
  note?: string | null;
  /** a note event: the screenshots and recordings added with the note (never sent: the line says so) */
  noteShots?: { images: number; videos: number };
  /** the item's kind when the event happened (a task's close is "Done") */
  itemKind?: ItemKind | null;
}

/** What kind of line it is, for the dialog's label ("Oct 4 · Note"). */
export type DraftLineKind = "note" | "stage" | "status" | "step" | "step_done" | "work";

export const DRAFT_LINE_LABEL: Record<DraftLineKind, string> = {
  note: "Note",
  stage: "Stage",
  status: "Status",
  step: "New sub-item",
  step_done: "Sub-item done",
  work: "Work",
};

/** One line the draft offers: the event it comes from, and its text. */
export interface DraftLine {
  eventId: string;
  at: string;
  kind: DraftLineKind;
  text: string;
}

const s = (v: unknown) => (typeof v === "string" ? v.trim() : "");
const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/**
 * " (1 screenshot, not sent)": what a note's line says about the files added with it. Send updates
 * posts text only, so the line mentions them instead of copying them. Pure.
 */
export function noteShotsText(shots: DraftEvent["noteShots"]): string {
  if (!shots || (!shots.images && !shots.videos)) return "";
  const parts = [...(shots.images ? [plural(shots.images, "screenshot")] : []), ...(shots.videos ? [plural(shots.videos, "screen recording")] : [])];
  return ` (${parts.join(" and ")}, not sent)`;
}
const withNote = (head: string, note: unknown) => (s(note) ? `${head}: ${s(note)}` : head);
const STAGE_RANK: Stage[] = ["todo", "developed", "deployed", "verified"];

/** The line one event makes, or null for an event Send updates never collects. Pure. */
export function draftLine(e: DraftEvent): DraftLine | null {
  const line = (kind: DraftLineKind, text: string): DraftLine | null => (text ? { eventId: e.id, at: e.at, kind, text } : null);
  const who = e.mine || !e.actor ? "" : `${e.actor}: `;
  // copied from the OEMUAP item itself: never offered back to it
  if (e.data.copied) return null;
  if (e.sub) {
    // a sub-item: made and done, by its title; its notes, under its title
    if (e.kind === "status" && e.data.to === "closed") return line("step_done", `Done: ${e.sub.title}`);
    if (e.kind === "note" && e.note) return line("note", `${who}${e.sub.title}: ${e.note}${noteShotsText(e.noteShots)}`);
    return null;
  }
  switch (e.kind) {
    case "note":
      return e.note ? line("note", `${who}${e.note}${noteShotsText(e.noteShots)}`) : null;
    case "stage": {
      const to = String(e.data.to) as Stage;
      const back = STAGE_RANK.indexOf(String(e.data.from) as Stage) > STAGE_RANK.indexOf(to);
      if (back && to === "todo") return line("stage", withNote("Sent back to To do", e.data.note));
      if (to === "developed") return line("stage", withNote("Marked developed", e.data.note));
      if (to === "deployed") return line("stage", withNote("Marked deployed", e.data.note));
      if (to === "verified") return line("stage", withNote("Verified", e.data.note));
      return null;
    }
    case "status": {
      if (e.data.to === "open") return line("status", "Reopened");
      const task = (e.data.kind ?? e.itemKind) === "task";
      return line("status", task ? withNote("Done", e.data.note) : withNote("Closed", e.data.note));
    }
    case "child":
      return e.data.added && s(e.data.title) ? line("step", `New step: ${s(e.data.title)}`) : null;
    case "work_finished": {
      const pr = s(e.data.prUrl);
      return line("work", `Worked on it: ${s(e.data.summary) || "a work session"}${pr ? ` (${pr})` : ""}`);
    }
    default:
      return null;
  }
}

/**
 * The lines waiting to be sent: every event since the link was made that makes a line and that no
 * earlier send dealt with (`dealt`: the ids of every send's events, sent or left out). Oldest first.
 */
export function waitingLines(events: DraftEvent[], dealt: Iterable<string>): DraftLine[] {
  const done = new Set(dealt);
  return events
    .filter((e) => !done.has(e.id))
    .map(draftLine)
    .filter((l): l is DraftLine => !!l)
    .sort((a, b) => a.at.localeCompare(b.at) || Number(BigInt(a.eventId) - BigInt(b.eventId)));
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "Oct 4", in the given time zone (the board's on the server, the browser's in the web app). Pure. */
export function shortDay(iso: string, timeZone?: string): string {
  const d = new Date(iso);
  if (!timeZone) return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone });
}

/** The note OEMUAP gets, from the ticked lines: "Update, Oct 4 to Oct 5:" and a line each. Pure. */
export function draftText(lines: DraftLine[], timeZone?: string): string {
  if (!lines.length) return "";
  const first = shortDay(lines[0]!.at, timeZone);
  const last = shortDay(lines[lines.length - 1]!.at, timeZone);
  const head = `Update, ${first}${last !== first ? ` to ${last}` : ""}:`;
  return [head, ...lines.map((l) => `- ${l.text.replace(/\n+/g, "\n  ")}`)].join("\n");
}

/** A number in the text that means something on the personal board, and something else on OEMUAP. */
export interface NumberWarning {
  /** as written: "#5" or "01-04" */
  ref: string;
  /** the personal item's title */
  title: string;
  message: string;
}

/**
 * Numbers in the text that are the personal board's (#5, 01-04): on OEMUAP the same number is a
 * different item, so the text would point people at the wrong thing. `items`: the personal board's
 * items (their #, current project number and title). Each number once, in the order written. Pure.
 */
export function numberWarnings(text: string, items: { num: number; key?: string | null; title: string }[]): NumberWarning[] {
  const byNum = new Map(items.map((i) => [i.num, i]));
  const byKey = new Map(items.filter((i) => i.key).map((i) => [i.key!, i]));
  const out: NumberWarning[] = [];
  const seen = new Set<string>();
  const rx = /(?<![\w#-])(#\d{1,6}\b|\d{2,4}-\d{2,6}\b)/g;
  for (const m of text.matchAll(rx)) {
    const ref = m[1]!;
    if (seen.has(ref)) continue;
    const it = ref.startsWith("#") ? byNum.get(Number(ref.slice(1))) : byKey.get(ref);
    if (!it) continue;
    seen.add(ref);
    out.push({ ref, title: it.title, message: `Your text says ${ref}: on your board that's "${it.title}". On OEMUAP ${ref} is a different item.` });
  }
  return out;
}

/** The text with each warned number replaced by its item's title, in quotes ("Use the title instead"). Pure. */
export function withTitles(text: string, warnings: NumberWarning[]): string {
  let out = text;
  for (const w of warnings) {
    const esc = w.ref.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    out = out.replace(new RegExp(`(?<![\\w#-])${esc}(?![\\w-])`, "g"), `"${w.title}"`);
  }
  return out;
}

/** The Send updates draft for a linked personal item: what the dialog (and Claude's draft_updates) shows. */
export interface SendDraft {
  /** the link it goes through: the send must name the same one */
  bridgeId: string;
  /** the OEMUAP item: "02-03", its #, its title (null when the person can't read it now) */
  label: string;
  num: number;
  title: string | null;
  /** the latest send through this link */
  lastSent: string | null;
  /** waiting to be sent, oldest first: one tick each */
  lines: DraftLine[];
  /** the note every line makes, in the board's time zone */
  text: string;
  /** numbers in `text` that are this personal board's */
  warnings: NumberWarning[];
  /** the personal board's numbers and titles, for warnings on the edited text */
  items: { num: number; key: string | null; title: string }[];
  /** what isn't sent: screenshots added since (add them on OEMUAP yourself) */
  notSent: { shots: number };
  /** posting with this key twice posts once */
  sendKey: string;
}

/**
 * Does a send cover exactly what's waiting? `eventIds` (sent) and `skipped` (left out) together must be
 * the waiting set, no more and no less, or more happened since the dialog opened: review it again. Pure.
 */
export function coversExactly(waiting: string[], eventIds: string[], skipped: string[]): boolean {
  const all = [...eventIds, ...skipped];
  const set = new Set(all);
  if (set.size !== all.length || set.size !== waiting.length) return false;
  return waiting.every((id) => set.has(id));
}
